import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, User, TrendingUp, Award, Mail, CheckCircle, AlertCircle, Brain, Heart, Activity } from 'lucide-react';
import { AthleteRadarChart } from '../charts';

export default function AthleteDetailModal({ athlete, onClose, allAthletes = [], loading = false, dataSource = 'internal' }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState(athlete?.email || '');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [generatedSplinkId, setGeneratedSplinkId] = useState('');
  
  // AI Insights state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Load AI insights when AI tab is opened (must be before conditional returns)
  useEffect(() => {
    if (activeTab === 'ai' && !aiAnalysis && !aiLoading && athlete) {
      loadAIInsights();
    }
  }, [activeTab, aiAnalysis, aiLoading, athlete]);

  const loadAIInsights = async () => {
    if (!athlete) return;
    setAiLoading(true);
    setAiError(null);
    try {
      // For external databases, pass the full athlete object
      const requestData = dataSource.startsWith('external_') ? {
        athlete: athlete,
        dataSource: dataSource,
        connectionId: dataSource.replace('external_', ''),
        userId: localStorage.getItem('userId') || '00000000-0000-0000-0000-000000000000',
        includeComparison: false  // Can't compare across databases
      } : {
        athleteId: athlete.id,
        dataSource: dataSource,
        includeComparison: true
      };
      
      const response = await fetch('/api/athlete-ai-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'analyze-athlete',
          data: requestData
        })
      });
      const result = await response.json();
      if (result.success) {
        console.log('🎯 AI Insights Response:', result);
        console.log('📊 Sport Scores:', result.sportScores);
        console.log('📈 Percentiles:', result.percentiles);
        setAiAnalysis(result);
      } else {
        setAiError(result.error || 'Failed to load AI insights');
      }
    } catch (error) {
      console.error('AI insights error:', error);
      setAiError('Failed to connect to AI service');
    } finally {
      setAiLoading(false);
    }
  };


  // Check if health data exists
  const hasHealthData = () => {
    return athlete.hemoglobin_level || athlete.blood_group || 
           athlete.blood_pressure_systolic || athlete.vo2_max ||
           athlete.resting_heart_rate || athlete.cooper_test_distance_m;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 max-w-md w-full">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
            <p className="text-slate-300">Loading athlete profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!athlete) return null;

  // Define key attributes for radar chart - check both naming conventions
  const attributeMap = {
    'height_cm': 'height',
    'weight_kg': 'weight', 
    'vertical_jump_cm': 'vertical_jump',
    'sprint_50m_sec': 'sprint_50m',
    'agility_sec': 'agility',
    'strength_kg': 'strength'
  };

  // Get actual attribute names from the athlete object
  const radarAttributes = Object.keys(athlete).filter(key => {
    // Include numeric columns that look like performance metrics
    const value = athlete[key];
    
    // Check if value is numeric (number or string that can be converted to number)
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    const isNumeric = !isNaN(numValue) && numValue !== null && value !== null && value !== '';
    
    const isMetric = key.includes('height') || key.includes('weight') || key.includes('jump') || 
                     key.includes('sprint') || key.includes('agility') || key.includes('strength') ||
                     key.includes('speed') || key.includes('endurance') || key.includes('_cm') || 
                     key.includes('_kg') || key.includes('_sec') || key.includes('_m') ||
                     key.includes('shuttle') || key.includes('test') || key.includes('throw') ||
                     key.includes('grip') || key.includes('shotput') || key.includes('reach');
    
    // Exclude non-performance fields
    const isExcluded = key === 'id' || key === 'age' || key.includes('created') || 
                       key.includes('updated') || key.includes('index') || key.includes('ratio') ||
                       key.includes('bmi') || key.includes('ape') || key.includes('leg_length') ||
                       key.includes('torso') || key.includes('power');
    
    return isNumeric && isMetric && !isExcluded;
  });

  // Calculate percentiles
  const calculatePercentile = (value, attribute) => {
    if (!allAthletes || allAthletes.length === 0) return null;
    
    const values = allAthletes
      .map(a => a[attribute])
      .filter(v => v != null)
      .sort((a, b) => a - b);
    
    if (values.length === 0) return null;
    
    // For time-based attributes (lower is better), invert the percentile
    const isLowerBetter = attribute.includes('sec');
    
    const rank = values.filter(v => isLowerBetter ? v > value : v < value).length;
    const percentile = (rank / values.length) * 100;
    
    return Math.round(percentile);
  };

  // Get sport recommendations based on attributes
  const getSportRecommendations = () => {
    const recommendations = [];
    
    // Get height value (try both naming conventions)
    const height = athlete.height_cm || athlete.height || 0;
    const verticalJump = athlete.vertical_jump_cm || athlete.vertical_jump || 0;
    const sprint = athlete.sprint_50m_sec || athlete.sprint_50m || athlete.sprint_time || 999;
    const agility = athlete.agility_sec || athlete.agility || athlete.agility_time || 999;
    
    if (height > 175 && verticalJump > 60) {
      recommendations.push({ sport: 'Basketball', score: 85, reason: 'Excellent height and jumping ability' });
    }
    if (sprint < 7 && agility < 12) {
      recommendations.push({ sport: 'Football', score: 82, reason: 'Great speed and agility' });
    }
    if (verticalJump > 55 && agility < 13) {
      recommendations.push({ sport: 'Volleyball', score: 78, reason: 'Good jumping and quick movements' });
    }
    if (sprint < 7.5) {
      recommendations.push({ sport: 'Track & Field', score: 75, reason: 'Strong sprinting performance' });
    }
    if (height > 170) {
      recommendations.push({ sport: 'Athletics', score: 70, reason: 'Good overall physical attributes' });
    }
    
    return recommendations.sort((a, b) => b.score - a.score).slice(0, 3);
  };

  const sportRecommendations = getSportRecommendations();

  // Handle sending invitation
  const handleSendInvitation = async () => {
    if (!inviteEmail) {
      setInviteError('Email is required');
      return;
    }

    setInviting(true);
    setInviteError('');

    try {
      const response = await fetch('/api/splink-board', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'splink.invite',
          data: {
            athleteId: athlete.id,
            email: inviteEmail
          }
        })
      });

      const result = await response.json();

      if (result.success) {
        setGeneratedSplinkId(result.splinkId);
        setInviteSuccess(true);
        // Close invite modal after 3 seconds
        setTimeout(() => {
          setShowInviteModal(false);
          setInviteSuccess(false);
        }, 3000);
      } else {
        setInviteError(result.error || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Invitation error:', error);
      setInviteError('An error occurred. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {athlete.full_name || athlete.first_name + ' ' + athlete.last_name}
              </h2>
              <p className="text-sm text-slate-400">
                {athlete.gender || 'N/A'} • {athlete.age || (athlete.date_of_birth ? new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear() : 'N/A')} years • {athlete.district_name || 'N/A'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-slate-700">
          <div className="flex space-x-1 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'overview'
                  ? 'text-blue-400'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4" />
                <span>Overview</span>
              </div>
              {activeTab === 'overview' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'ai'
                  ? 'text-blue-400'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Brain className="w-4 h-4" />
                <span>AI Insights</span>
              </div>
              {activeTab === 'ai' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'health'
                  ? 'text-blue-400'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Heart className="w-4 h-4" />
                <span>Health</span>
              </div>
              {activeTab === 'health' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6 space-y-6">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <>
              {/* Splink ID Section - Only show for internal database */}
              {dataSource === 'internal' && !athlete.has_splink_account ? (
            <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1 flex items-center space-x-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <span>Enable Assessment Access</span>
                  </h4>
                  <p className="text-sm text-slate-400">
                    Send invitation to allow this athlete to take assessments
                  </p>
                </div>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>Assign Splink ID</span>
                </button>
              </div>
            </div>
          ) : dataSource === 'internal' && athlete.has_splink_account ? (
            <div className="bg-green-900/20 border border-green-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1 flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span>Splink ID: {athlete.splink_id}</span>
                  </h4>
                  <p className="text-sm text-slate-400">
                    {athlete.invitation_sent 
                      ? `Invitation sent ${athlete.invitation_sent_at ? new Date(athlete.invitation_sent_at).toLocaleDateString() : ''}`
                      : 'Account active'}
                  </p>
                  {athlete.last_assessment_at && (
                    <p className="text-xs text-green-400 mt-1">
                      Last assessment: {new Date(athlete.last_assessment_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Radar Chart */}
          {radarAttributes.length >= 3 && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                <span>Performance Profile</span>
              </h3>
              <div className="h-80">
                <AthleteRadarChart
                  athletes={[athlete]}
                  attributes={radarAttributes}
                />
              </div>
            </div>
          )}

          {/* Key Metrics with Percentiles */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-4">Key Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {radarAttributes.map(attr => {
                const value = athlete[attr];
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                
                // Use pre-calculated percentiles if available (from normalization), otherwise calculate on-the-fly
                let percentile = null;
                if (athlete.percentiles && athlete.percentiles[attr] !== undefined) {
                  percentile = athlete.percentiles[attr];
                } else {
                  percentile = calculatePercentile(numValue, attr);
                }
                
                const label = attr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                
                return (
                  <div key={attr} className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">{label}</p>
                    <p className="text-lg font-bold text-white">{numValue?.toFixed(1)}</p>
                    {percentile !== null && percentile !== undefined && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Percentile</span>
                          <span className={`font-semibold ${
                            percentile >= 75 ? 'text-green-400' :
                            percentile >= 50 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>
                            {percentile}th
                          </span>
                        </div>
                        <div className="w-full bg-slate-700 rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${
                              percentile >= 75 ? 'bg-green-400' :
                              percentile >= 50 ? 'bg-yellow-400' :
                              'bg-orange-400'
                            }`}
                            style={{ width: `${percentile}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

              {/* Sport Recommendations */}
              {sportRecommendations.length > 0 && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Award className="w-5 h-5 text-yellow-400" />
                <span>Recommended Sports</span>
              </h3>
              <div className="space-y-3">
                {sportRecommendations.map((rec, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-white">{rec.sport}</span>
                      <span className="text-sm font-bold text-blue-400">{rec.score}% Match</span>
                    </div>
                    <p className="text-sm text-slate-400">{rec.reason}</p>
                    <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                        style={{ width: `${rec.score}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

              {/* Additional Info */}
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
            <h3 className="text-lg font-semibold text-white mb-3">Additional Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {athlete.school_name && (
                <div>
                  <span className="text-slate-400">School:</span>
                  <span className="text-white ml-2">{athlete.school_name}</span>
                </div>
              )}
              {athlete.date_of_birth && (
                <div>
                  <span className="text-slate-400">Date of Birth:</span>
                  <span className="text-white ml-2">{new Date(athlete.date_of_birth).toLocaleDateString()}</span>
                </div>
              )}
              {athlete.contact_number && (
                <div>
                  <span className="text-slate-400">Contact:</span>
                  <span className="text-white ml-2">{athlete.contact_number}</span>
                </div>
              )}
              {athlete.email && (
                <div>
                  <span className="text-slate-400">Email:</span>
                  <span className="text-white ml-2">{athlete.email}</span>
                </div>
              )}
            </div>
              </div>
            </>
          )}

          {/* AI INSIGHTS TAB */}
          {activeTab === 'ai' && (
            <>
              {aiLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mb-4"></div>
                  <p className="text-slate-300">AI is analyzing athlete profile...</p>
                </div>
              ) : aiError ? (
                <div className="bg-red-900/20 border border-red-600 rounded-lg p-6 text-center">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-400 font-semibold mb-2">Failed to load AI insights</p>
                  <p className="text-slate-400 text-sm mb-4">{aiError}</p>
                  <button
                    onClick={loadAIInsights}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : aiAnalysis ? (
                <>
                  {/* AI Analysis */}
                  <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-blue-500/30 p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                      <Brain className="w-6 h-6 text-blue-400" />
                      <span>AI Performance Analysis</span>
                    </h3>
                    <div className="prose prose-invert max-w-none">
                      <ReactMarkdown
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mt-6 mb-3" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-xl font-bold text-white mt-5 mb-2" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-blue-400 mt-4 mb-2" {...props} />,
                          p: ({node, ...props}) => <p className="text-slate-300 mb-3 leading-relaxed" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc list-inside text-slate-300 mb-3 space-y-1 ml-4" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal list-inside text-slate-300 mb-3 space-y-1 ml-4" {...props} />,
                          li: ({node, ...props}) => <li className="text-slate-300 ml-2" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-bold text-white" {...props} />,
                          em: ({node, ...props}) => <em className="italic text-blue-300" {...props} />,
                          code: ({node, ...props}) => <code className="bg-slate-800 px-1.5 py-0.5 rounded text-green-400 text-sm" {...props} />,
                        }}
                      >
                        {aiAnalysis.analysis}
                      </ReactMarkdown>
                    </div>
                  </div>

                  {/* Sport Suitability Scores */}
                  {aiAnalysis.sportScores && (
                    <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                        <Award className="w-5 h-5 text-yellow-400" />
                        <span>Sport Suitability Scores</span>
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(aiAnalysis.sportScores)
                          .sort((a, b) => b[1].score - a[1].score)
                          .slice(0, 5)
                          .map(([sport, data]) => (
                            <div key={sport} className="bg-slate-800/50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-white capitalize">{sport}</span>
                                <span className="text-lg font-bold text-blue-400">{data.score}/100</span>
                              </div>
                              <div className="w-full bg-slate-700 rounded-full h-2 mb-3">
                                <div
                                  className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                                  style={{ width: `${data.score}%` }}
                                ></div>
                              </div>
                              {data.strengths.length > 0 && (
                                <div className="text-xs text-green-400 mb-1">
                                  ✓ Strengths: {data.strengths.map(s => s.attribute).join(', ')}
                                </div>
                              )}
                              {data.weaknesses.length > 0 && (
                                <div className="text-xs text-orange-400">
                                  ⚠ Needs work: {data.weaknesses.map(w => w.attribute).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                </>
              ) : null}
            </>
          )}

          {/* HEALTH TAB */}
          {activeTab === 'health' && (
            <>
              {hasHealthData() ? (
                <>
                  {/* Blood Test Results */}
                  {(athlete.hemoglobin_level || athlete.blood_group) && (
                    <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                        <Heart className="w-5 h-5 text-red-400" />
                        <span>Blood Test Results</span>
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        {athlete.hemoglobin_level && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-400 mb-1">Hemoglobin Level</p>
                            <p className="text-2xl font-bold text-white">{athlete.hemoglobin_level} g/dL</p>
                            <p className="text-xs text-green-400 mt-1">✓ Normal range (13-17 g/dL)</p>
                          </div>
                        )}
                        {athlete.blood_group && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-400 mb-1">Blood Group</p>
                            <p className="text-2xl font-bold text-white">{athlete.blood_group}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cardiovascular Health */}
                  {(athlete.resting_heart_rate || athlete.blood_pressure_systolic || athlete.vo2_max) && (
                    <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                        <Activity className="w-5 h-5 text-blue-400" />
                        <span>Cardiovascular Health</span>
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {athlete.resting_heart_rate && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-400 mb-1">Resting Heart Rate</p>
                            <p className="text-2xl font-bold text-white">{athlete.resting_heart_rate} bpm</p>
                            <p className="text-xs text-green-400 mt-1">✓ Good (60-100 bpm)</p>
                          </div>
                        )}
                        {athlete.blood_pressure_systolic && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-400 mb-1">Blood Pressure</p>
                            <p className="text-2xl font-bold text-white">{athlete.blood_pressure_systolic}/{athlete.blood_pressure_diastolic || '?'}</p>
                            <p className="text-xs text-green-400 mt-1">✓ Normal</p>
                          </div>
                        )}
                        {athlete.vo2_max && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <p className="text-xs text-slate-400 mb-1">VO2 Max</p>
                            <p className="text-2xl font-bold text-white">{athlete.vo2_max}</p>
                            <p className="text-xs text-slate-400 mt-1">ml/kg/min</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cooper Test */}
                  {athlete.cooper_test_distance_m && (
                    <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                      <h3 className="text-lg font-semibold text-white mb-4">Cooper Test</h3>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">Distance Covered (12 minutes)</p>
                        <p className="text-3xl font-bold text-white">{athlete.cooper_test_distance_m}m</p>
                        <div className="mt-3 w-full bg-slate-700 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full"
                            style={{ width: `${Math.min((athlete.cooper_test_distance_m / 3000) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">Target: 2,800m+ for excellent fitness</p>
                      </div>
                    </div>
                  )}

                  {/* Overall Health Status */}
                  <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl border border-green-500/30 p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                      <div>
                        <h3 className="text-xl font-bold text-white">Overall Health Status</h3>
                        <p className="text-sm text-green-400">All metrics within healthy range</p>
                      </div>
                    </div>
                    <p className="text-slate-300 text-sm">
                      Based on available health data, this athlete shows good overall health indicators. 
                      Continue regular monitoring and maintain current fitness routines.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* No Health Data - Show Desired Metrics */}
                  <div className="bg-blue-900/20 border border-blue-600/30 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                      <AlertCircle className="w-5 h-5 text-blue-400" />
                      <span>No Health Data Available</span>
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Health metrics have not been recorded for this athlete. Consider conducting the following assessments:
                    </p>
                  </div>

                  {/* Recommended Health Metrics */}
                  <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Recommended Health Assessments</h3>
                    <div className="space-y-4">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h4 className="text-white font-semibold mb-2 flex items-center space-x-2">
                          <Heart className="w-4 h-4 text-red-400" />
                          <span>Blood Tests</span>
                        </h4>
                        <ul className="text-sm text-slate-400 space-y-1 ml-6">
                          <li>• Hemoglobin Level (13-17 g/dL for optimal performance)</li>
                          <li>• Blood Group (for medical records)</li>
                          <li>• Platelet Count (150-400k/μL)</li>
                          <li>• WBC Count (4k-11k/μL)</li>
                        </ul>
                      </div>

                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h4 className="text-white font-semibold mb-2 flex items-center space-x-2">
                          <Activity className="w-4 h-4 text-blue-400" />
                          <span>Cardiovascular Tests</span>
                        </h4>
                        <ul className="text-sm text-slate-400 space-y-1 ml-6">
                          <li>• Resting Heart Rate (60-100 bpm)</li>
                          <li>• Blood Pressure (120/80 mmHg ideal)</li>
                          <li>• VO2 Max (aerobic capacity)</li>
                          <li>• Cooper Test (12-minute run distance)</li>
                        </ul>
                      </div>

                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h4 className="text-white font-semibold mb-2 flex items-center space-x-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <span>Additional Metrics</span>
                        </h4>
                        <ul className="text-sm text-slate-400 space-y-1 ml-6">
                          <li>• Oxygen Saturation (95-100%)</li>
                          <li>• Lung Capacity</li>
                          <li>• Bone Density (for injury prevention)</li>
                          <li>• Body Fat Percentage</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Average Desired Values */}
                  <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700">
                    <h3 className="text-lg font-semibold text-white mb-4">Target Health Values for Age {athlete.age}</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">Hemoglobin</p>
                        <p className="text-xl font-bold text-white">13-17 g/dL</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">Resting HR</p>
                        <p className="text-xl font-bold text-white">60-80 bpm</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">Blood Pressure</p>
                        <p className="text-xl font-bold text-white">120/80</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">VO2 Max</p>
                        <p className="text-xl font-bold text-white">45-55</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">Cooper Test</p>
                        <p className="text-xl font-bold text-white">2,800m+</p>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <p className="text-xs text-slate-400 mb-1">O2 Saturation</p>
                        <p className="text-xl font-bold text-white">95-100%</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Invitation Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">Assign Splink ID & Send Invitation</h3>
              
              {!inviteSuccess ? (
                <>
                  <div className="mb-4">
                    <p className="text-sm text-slate-400 mb-4">
                      Athlete: <span className="text-white font-semibold">{athlete.first_name} {athlete.last_name}</span>
                    </p>
                    
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="Enter athlete's email"
                      disabled={inviting}
                    />
                  </div>

                  {inviteError && (
                    <div className="mb-4 bg-red-900/20 border border-red-600 rounded-lg p-3">
                      <p className="text-red-400 text-sm flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>{inviteError}</span>
                      </p>
                    </div>
                  )}

                  <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3 mb-4">
                    <p className="text-xs text-blue-300">
                      <strong>What happens next:</strong>
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-400">
                      <li>• A unique Splink ID will be generated</li>
                      <li>• An email invitation will be sent</li>
                      <li>• Athlete can enter Splink ID during onboarding</li>
                      <li>• Their account will be automatically linked</li>
                    </ul>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteError('');
                      }}
                      className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                      disabled={inviting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendInvitation}
                      disabled={inviting || !inviteEmail}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      {inviting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4" />
                          <span>Send Invitation</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Invitation Sent!</h4>
                  <p className="text-sm text-slate-400 mb-4">
                    Splink ID: <span className="text-blue-400 font-mono font-bold">{generatedSplinkId}</span>
                  </p>
                  <div className="bg-green-900/20 border border-green-600 rounded-lg p-4 text-left">
                    <p className="text-sm text-green-300 mb-2">
                      <strong>Email sent to:</strong> {inviteEmail}
                    </p>
                    <p className="text-xs text-slate-400">
                      The athlete will receive an email with their Splink ID. They can enter it during onboarding to link their account.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
